import React, { useState, useEffect } from 'react';
import config from '../../config';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  useTheme,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import EditIcon from '@mui/icons-material/Edit';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import { useLanguage } from '../../contexts/LanguageContext';
import { PurchaseReturn } from '../../types/purchaseReturns.types';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';

interface PurchaseReturnPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  purchaseReturn: PurchaseReturn | null;
  onEdit?: () => void;
}

const PurchaseReturnPreviewDialog: React.FC<PurchaseReturnPreviewDialogProps> = ({
  open,
  onClose,
  purchaseReturn,
  onEdit
}) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);

  useEffect(() => {
    if (open) {
      loadCompanyInfo();
    }
  }, [open, purchaseReturn]);

  const loadCompanyInfo = async () => {
    try {
      const info = await companyInfoService.getCompanyInfo();
      setCompanyInfo(info);
    } catch (error) {
      console.error('Failed to load company info:', error);
    }
  };

  if (!purchaseReturn) return null;

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'Draft': return isRTL ? 'مسودة' : 'Draft';
      case 'Posted': return isRTL ? 'مرحّل' : 'Posted';
      case 'Cancelled': return isRTL ? 'ملغي' : 'Cancelled';
      default: return status || '-';
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'Draft': return '#9e9e9e';
      case 'Posted': return '#4caf50';
      case 'Cancelled': return '#f44336';
      default: return '#9e9e9e';
    }
  };

  const getStatusChipColor = (status?: string) => {
    switch (status) {
      case 'Draft': return 'default';
      case 'Posted': return 'success';
      case 'Cancelled': return 'error';
      default: return 'default';
    }
  };

  const getPaymentMethodLabel = (method?: string) => {
    switch (method) {
      case 'Supplier': return isRTL ? 'مورد' : 'Supplier';
      case 'Custody': return isRTL ? 'عهدة' : 'Custody';
      case 'PaymentAccount': return isRTL ? 'حساب دفع' : 'Payment Account';
      default: return method || '-';
    }
  };

  const getPaymentMethodColor = (method?: string) => {
    switch (method) {
      case 'Supplier': return '#2196f3';
      case 'Custody': return '#9c27b0';
      case 'PaymentAccount': return '#ff9800';
      default: return '#9e9e9e';
    }
  };

  const getPayeeName = () => {
    switch (purchaseReturn.paymentMethod) {
      case 'Supplier':
        return isRTL ? (purchaseReturn.supplierNameAr || purchaseReturn.supplierName) : purchaseReturn.supplierName;
      case 'Custody':
        return isRTL ? (purchaseReturn.custodyNameAr || purchaseReturn.custodyName) : purchaseReturn.custodyName;
      case 'PaymentAccount':
        return isRTL ? (purchaseReturn.paymentAccountNameAr || purchaseReturn.paymentAccountName) : purchaseReturn.paymentAccountName;
      default: return '-';
    }
  };

  const getPayeeCode = () => {
    switch (purchaseReturn.paymentMethod) {
      case 'Supplier': return purchaseReturn.supplierCode;
      case 'Custody': return purchaseReturn.custodyCode;
      case 'PaymentAccount': return purchaseReturn.paymentAccountCode;
      default: return '';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB');
  };

  const formatCurrency = (amount?: number) => {
    if (amount === undefined || amount === null) return '0.000';
    return amount.toFixed(3);
  };

  const totalAmount = purchaseReturn.totalAmount ?? purchaseReturn.details.reduce((sum, d) => sum + d.totalPrice, 0);

  const handlePrint = () => {
    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (!printWindow) return;

    const dir = isRTL ? 'rtl' : 'ltr';
    const align = isRTL ? 'right' : 'left';
    const alignOpp = isRTL ? 'left' : 'right';
    const currency = isRTL ? 'د.ك' : 'KWD';
    const themeColor = '#7b1fa2';

    let itemsRows = '';
    purchaseReturn.details.forEach((detail, idx) => {
      itemsRows += `
        <tr>
          <td style="text-align:center;">${idx + 1}</td>
          <td>
            <strong>${detail.itemCode}</strong><br/>
            <span style="color:#666;font-size:11px;">${isRTL ? detail.itemNameAr : detail.itemName}</span>
          </td>
          <td style="text-align:center;">${isRTL ? detail.unitNameAr : detail.unitName}</td>
          <td style="text-align:center;font-weight:bold;">${formatCurrency(detail.quantity)}</td>
          <td style="text-align:center;font-weight:bold;">${formatCurrency(detail.unitPrice)}</td>
          <td style="text-align:center;font-weight:bold;color:#7b1fa2;">${formatCurrency(detail.totalPrice)}</td>
          <td style="font-size:11px;">${detail.notes || '-'}</td>
        </tr>
      `;
    });

    printWindow.document.write(`
      <html dir="${dir}">
      <head>
        <title>${isRTL ? 'مرتجع مشتريات' : 'Purchase Return'} - ${purchaseReturn.returnNumber}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; padding: 20px; direction: ${dir}; color: #333; font-size: 13px; }
          .header { text-align: center; border-bottom: 3px solid ${themeColor}; padding-bottom: 15px; margin-bottom: 15px; }
          .header img { max-height: 60px; margin-bottom: 8px; }
          .header h2 { color: ${themeColor}; margin: 5px 0; font-size: 20px; }
          .header .subtitle { font-size: 11px; color: #666; }
          .doc-title { text-align: center; margin-bottom: 20px; }
          .doc-title h3 { color: ${themeColor}; font-size: 24px; border: 2px solid ${themeColor}; display: inline-block; padding: 8px 30px; border-radius: 8px; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 15px; border: 1px solid #ddd; border-radius: 8px; padding: 15px; }
          .info-item label { font-size: 11px; color: #888; display: block; margin-bottom: 2px; }
          .info-item .value { font-size: 14px; font-weight: bold; }
          .info-item .value.primary { color: #7b1fa2; font-size: 16px; }
          .status-badge { display: inline-block; padding: 3px 12px; border-radius: 12px; font-size: 12px; font-weight: bold; color: white; }
          .payee-box { background: #f3e5f5; border: 1px solid #ce93d8; border-radius: 8px; padding: 12px; margin-bottom: 10px; }
          .payee-box label { font-size: 11px; color: #666; }
          .payee-box .name { font-size: 16px; font-weight: bold; color: #7b1fa2; }
          .payee-box .code { font-size: 11px; color: #888; }
          .payee-box .method-badge { display: inline-block; padding: 2px 10px; border-radius: 10px; font-size: 11px; font-weight: bold; color: white; margin-${isRTL ? 'right' : 'left'}: 8px; }
          .warehouse-box { background: #e8f5e9; border: 1px solid #a5d6a7; border-radius: 8px; padding: 12px; margin-bottom: 10px; }
          .warehouse-box label { font-size: 11px; color: #666; }
          .warehouse-box .name { font-size: 14px; font-weight: bold; color: #2e7d32; }
          .warehouse-box .code { font-size: 11px; color: #888; }
          .reason-box { background: #fce4ec; border: 1px solid #f48fb1; border-radius: 8px; padding: 10px; margin-bottom: 10px; }
          .reason-box label { font-size: 11px; color: #888; font-weight: bold; }
          .notes-box { background: #f3e5f5; border: 1px solid #ce93d8; border-radius: 8px; padding: 10px; margin-bottom: 15px; }
          .notes-box label { font-size: 11px; color: #888; font-weight: bold; }
          .section-title { background: ${themeColor}; color: white; padding: 10px 15px; font-weight: bold; font-size: 14px; border-radius: 8px 8px 0 0; }
          table { width: 100%; border-collapse: collapse; }
          thead th { background: #f3e5f5; color: #4a148c; padding: 10px 6px; font-size: 11px; text-align: ${align}; border-bottom: 2px solid #ce93d8; }
          thead th.center { text-align: center; }
          tbody td { padding: 7px 6px; border-bottom: 1px solid #eee; font-size: 12px; }
          tbody tr:nth-child(even) { background: #fafafa; }
          .table-wrapper { border: 1px solid #ddd; border-radius: 0 0 8px 8px; overflow: hidden; margin-bottom: 20px; }
          .total-row { background: #7b1fa2; color: white; }
          .total-row td { font-weight: bold; padding: 10px 6px; font-size: 14px; }
          .signatures { display: flex; justify-content: space-between; margin-top: 60px; }
          .sig-block { text-align: center; width: 30%; }
          .sig-line { border-top: 1px solid #333; padding-top: 8px; margin-top: 60px; font-weight: bold; font-size: 12px; }
          .print-date { text-align: ${alignOpp}; font-size: 10px; color: #999; margin-bottom: 10px; }
          @media print { @page { size: A4; margin: 10mm; } }
        </style>
      </head>
      <body>
        <div class="print-date">${isRTL ? 'تاريخ الطباعة:' : 'Print Date:'} ${new Date().toLocaleString(isRTL ? 'ar-KW' : 'en-GB')}</div>

        ${companyInfo ? `
        <div class="header">
          ${companyInfo.logoPath ? `<img src="${config.API_ORIGIN}${companyInfo.logoPath}" onerror="this.style.display='none'" />` : ''}
          <h2>${isRTL ? companyInfo.companyNameAr : companyInfo.companyName}</h2>
          <div class="subtitle">
            ${companyInfo.address ? companyInfo.address + ' | ' : ''}${companyInfo.phone ? companyInfo.phone + ' | ' : ''}${companyInfo.email || ''}${companyInfo.website ? ' | ' + companyInfo.website : ''}
          </div>
        </div>
        ` : ''}

        <div class="doc-title">
          <h3>${isRTL ? 'مرتجع مشتريات' : 'Purchase Return'}</h3>
        </div>

        <div class="info-grid">
          <div class="info-item">
            <label>${isRTL ? 'رقم المرتجع' : 'Return Number'}</label>
            <div class="value primary">${purchaseReturn.returnNumber}</div>
          </div>
          <div class="info-item">
            <label>${isRTL ? 'التاريخ' : 'Date'}</label>
            <div class="value">${formatDate(purchaseReturn.returnDate)}</div>
          </div>
          <div class="info-item">
            <label>${isRTL ? 'الحالة' : 'Status'}</label>
            <div><span class="status-badge" style="background:${getStatusColor(purchaseReturn.status)}">${getStatusLabel(purchaseReturn.status)}</span></div>
          </div>
          ${purchaseReturn.purchaseInvoiceNumber ? `
          <div class="info-item">
            <label>${isRTL ? 'فاتورة المشتريات' : 'Purchase Invoice'}</label>
            <div class="value" style="color:#2196f3;">${purchaseReturn.purchaseInvoiceNumber}</div>
          </div>
          ` : `
          <div class="info-item">
            <label>${isRTL ? 'فاتورة المشتريات' : 'Purchase Invoice'}</label>
            <div class="value" style="color:#999;">${isRTL ? 'بدون فاتورة' : 'No Invoice'}</div>
          </div>
          `}
        </div>

        <div class="payee-box">
          <label>${isRTL ? 'المدفوع له' : 'Payee'} <span class="method-badge" style="background:${getPaymentMethodColor(purchaseReturn.paymentMethod)}">${getPaymentMethodLabel(purchaseReturn.paymentMethod)}</span></label>
          <div class="name">${getPayeeName() || '-'}</div>
          <div class="code">${getPayeeCode() || ''}</div>
        </div>

        <div class="warehouse-box">
          <label>${isRTL ? 'المخزن' : 'Warehouse'}</label>
          <div class="name">${isRTL ? purchaseReturn.warehouseNameAr : purchaseReturn.warehouseName}</div>
          <div class="code">${purchaseReturn.warehouseCode || ''}</div>
        </div>

        ${purchaseReturn.returnReason ? `
        <div class="reason-box">
          <label>${isRTL ? 'سبب الإرجاع:' : 'Return Reason:'}</label>
          <div>${purchaseReturn.returnReason}</div>
        </div>
        ` : ''}

        ${purchaseReturn.notes ? `
        <div class="notes-box">
          <label>${isRTL ? 'ملاحظات:' : 'Notes:'}</label>
          <div>${purchaseReturn.notes}</div>
        </div>
        ` : ''}

        <div class="section-title">📦 ${isRTL ? 'تفاصيل المرتجع' : 'Return Details'}</div>
        <div class="table-wrapper">
          <table>
            <thead>
              <tr>
                <th class="center">#</th>
                <th>${isRTL ? 'الصنف' : 'Item'}</th>
                <th class="center">${isRTL ? 'الوحدة' : 'Unit'}</th>
                <th class="center">${isRTL ? 'الكمية' : 'Qty'}</th>
                <th class="center">${isRTL ? 'السعر' : 'Price'}</th>
                <th class="center">${isRTL ? 'الإجمالي' : 'Total'}</th>
                <th>${isRTL ? 'ملاحظات' : 'Notes'}</th>
              </tr>
            </thead>
            <tbody>
              ${itemsRows}
              <tr class="total-row">
                <td colspan="5" style="text-align:${alignOpp};">${isRTL ? 'الإجمالي الكلي:' : 'Total Amount:'}</td>
                <td style="text-align:center;font-size:16px;">${formatCurrency(totalAmount)} ${currency}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="signatures">
          <div class="sig-block"><div class="sig-line">${isRTL ? 'المعتمد' : 'Approved By'}</div></div>
          <div class="sig-block"><div class="sig-line">${isRTL ? 'أمين المخزن' : 'Warehouse Keeper'}</div></div>
          <div class="sig-block"><div class="sig-line">${isRTL ? 'المحاسب' : 'Accountant'}</div></div>
        </div>
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      // printWindow stays open for user review
    }, 500);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      dir={isRTL ? 'rtl' : 'ltr'}
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: isDarkMode ? '#1a1a2e' : 'white'
        }
      }}
    >
      <DialogTitle
        sx={{
          background: isDarkMode
            ? 'linear-gradient(135deg, #4a148c 0%, #7b1fa2 100%)'
            : 'linear-gradient(135deg, #7b1fa2 0%, #ab47bc 100%)',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssignmentReturnIcon />
          <Box component="span">
            {isRTL ? 'معاينة مرتجع مشتريات' : 'Purchase Return Preview'}
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 3 }}>
        {/* Header Info */}
        <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5', borderRadius: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{isRTL ? 'رقم المرتجع' : 'Return Number'}</Typography>
              <Typography variant="h6" fontWeight="bold" sx={{ color: '#7b1fa2' }}>{purchaseReturn.returnNumber}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{isRTL ? 'التاريخ' : 'Date'}</Typography>
              <Typography variant="body1" component="div" fontWeight="bold">{formatDate(purchaseReturn.returnDate)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{isRTL ? 'الحالة' : 'Status'}</Typography>
              <Box><Chip label={getStatusLabel(purchaseReturn.status)} color={getStatusChipColor(purchaseReturn.status)} size="small" sx={{ fontWeight: 'bold' }} /></Box>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{isRTL ? 'فاتورة المشتريات' : 'Purchase Invoice'}</Typography>
              <Typography variant="body1" fontWeight="bold" color={purchaseReturn.purchaseInvoiceNumber ? 'info.main' : 'text.disabled'}>
                {purchaseReturn.purchaseInvoiceNumber || (isRTL ? 'بدون فاتورة' : 'No Invoice')}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Payee Info */}
          <Box sx={{ p: 2, bgcolor: isDarkMode ? 'rgba(123,31,162,0.1)' : '#f3e5f5', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {isRTL ? 'المدفوع له' : 'Payee'}
              </Typography>
              <Chip label={getPaymentMethodLabel(purchaseReturn.paymentMethod)} size="small" sx={{ fontSize: '0.65rem', height: 20, bgcolor: getPaymentMethodColor(purchaseReturn.paymentMethod), color: 'white', fontWeight: 'bold' }} />
            </Box>
            <Typography variant="h6" fontWeight="bold" sx={{ color: '#7b1fa2' }}>{getPayeeName() || '-'}</Typography>
            <Typography variant="caption" color="text.secondary">{getPayeeCode()}</Typography>
          </Box>

          {/* Warehouse Info */}
          <Box sx={{ mt: 2, p: 2, bgcolor: isDarkMode ? 'rgba(76,175,80,0.1)' : '#e8f5e9', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{isRTL ? 'المخزن' : 'Warehouse'}</Typography>
            <Typography variant="body1" fontWeight="bold" color="success.main">{isRTL ? purchaseReturn.warehouseNameAr : purchaseReturn.warehouseName}</Typography>
            <Typography variant="caption" color="text.secondary">{purchaseReturn.warehouseCode}</Typography>
          </Box>

          {purchaseReturn.returnReason && (
            <Box sx={{ mt: 2, p: 2, bgcolor: isDarkMode ? 'rgba(233,30,99,0.1)' : '#fce4ec', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>{isRTL ? 'سبب الإرجاع:' : 'Return Reason:'}</Typography>
              <Typography variant="body2">{purchaseReturn.returnReason}</Typography>
            </Box>
          )}

          {purchaseReturn.notes && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>{isRTL ? 'ملاحظات:' : 'Notes:'}</Typography>
              <Typography variant="body2">{purchaseReturn.notes}</Typography>
            </Box>
          )}
        </Paper>

        {/* Items Details */}
        <Paper elevation={2} sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ bgcolor: isDarkMode ? 'rgba(123,31,162,0.3)' : '#7b1fa2', color: 'white', p: 1.5, fontWeight: 'bold' }}>
            {isRTL ? '📦 تفاصيل المرتجع' : '📦 Return Details'}
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الصنف' : 'Item'}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الوحدة' : 'Unit'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الكمية' : 'Qty'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'السعر' : 'Price'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الإجمالي' : 'Total'}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'ملاحظات' : 'Notes'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {purchaseReturn.details.map((detail, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{idx + 1}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>
                      <strong>{detail.itemCode}</strong><br />
                      <Typography variant="caption" color="text.secondary">{isRTL ? detail.itemNameAr : detail.itemName}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{isRTL ? detail.unitNameAr : detail.unitName}</TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.85rem', fontWeight: 'bold' }}>{formatCurrency(detail.quantity)}</TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.85rem', fontWeight: 'bold' }}>{formatCurrency(detail.unitPrice)}</TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#7b1fa2' }}>{formatCurrency(detail.totalPrice)}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{detail.notes || '-'}</TableCell>
                  </TableRow>
                ))}
                
                <TableRow>
                  <TableCell colSpan={5} align="right" sx={{ fontWeight: 'bold', fontSize: '1rem', bgcolor: isDarkMode ? 'rgba(123,31,162,0.2)' : 'rgba(123,31,162,0.1)' }}>
                    {isRTL ? 'الإجمالي الكلي:' : 'Total Amount:'}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: 'success.main', bgcolor: isDarkMode ? 'rgba(123,31,162,0.2)' : 'rgba(123,31,162,0.1)' }}>
                    {formatCurrency(totalAmount)} {isRTL ? 'د.ك' : 'KWD'}
                  </TableCell>
                  <TableCell sx={{ bgcolor: isDarkMode ? 'rgba(123,31,162,0.2)' : 'rgba(123,31,162,0.1)' }}></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5' }}>
        <Button onClick={onClose} size="large">{isRTL ? 'إغلاق' : 'Close'}</Button>
        {purchaseReturn?.status === 'Draft' && onEdit && (
          <Button variant="outlined" startIcon={<EditIcon />} onClick={() => { onEdit(); onClose(); }} size="large" color="warning">{isRTL ? 'تعديل' : 'Edit'}</Button>
        )}
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          size="large"
          sx={{ background: isDarkMode ? 'linear-gradient(45deg, #4a148c 30%, #7b1fa2 90%)' : 'linear-gradient(45deg, #7b1fa2 30%, #ab47bc 90%)' }}
        >
          {isRTL ? 'طباعة' : 'Print'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PurchaseReturnPreviewDialog;